import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Space,
  Button,
  Tag,
  Table,
  Select,
  Badge,
  Tooltip,
  Modal,
  message,
  Spin
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  RiseOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
  MailOutlined,
  PhoneOutlined,
  LineChartOutlined,
  ReloadOutlined,
  FilterOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { AuthContext } from '../../../../../context/authContext';
import axiosConfig from '../../../../../config/axiosConfig';
import './SolutionPartnerAdminDashboard.css';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;

const SolutionPartnerAdminDashboard = () => {
  const { currentUser: _currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('4m'); // 4 months (current + 3 previous)
  const [_dateRange, _setDateRange] = useState([moment().subtract(3, 'months'), moment()]);
  
  // Dashboard data from API (same structure as SuperAdminDashboard)
  const [dashboardCounts, setDashboardCounts] = useState({
    totalcustomer: 0,
    directcustomer: 0,
    solutioncustomer: 0,
    newcustomerthismonth: 0,
    pendingcustomer: 0,
    customerviasolution: 0,
    activesolutioncustomer: 0,
    pendingsolutioncustomer: 0,
    activedirectcustomer: 0,
    pendingdirectcustomer: 0,
    monthlycustomer: []
  });

  // Monthly chart data state
  const [monthlyChartData, setMonthlyChartData] = useState([]);

  // Table data and loading
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Company details state
  const [companyDetails, setCompanyDetails] = useState({
    companies: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      limit: 20,
      hasNextPage: false,
      hasPrevPage: false
    },
    totalrecords: 0,
    totalreffredcustomer: 0,
    totalsolutionpartners: 0
  });

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
    fetchMonthlySignupData();
    fetchCustomerData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get('/dashboard/counts');
      
      if (response.data?.status === true) {
        setDashboardCounts(response.data.entity);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      message.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch monthly signup data from dedicated API
  const fetchMonthlySignupData = async () => {
    try {
      const response = await axiosConfig.get('/dashboard/monthly-signup-count');
      
      if (response.data?.status === true) {
        const monthlyData = response.data.entity?.map(item => ({
          month: item.month_year,
          monthShort: item.month_year?.substring(0, 3) || '',
          signups: parseInt(item.totalcustomer) || 0,
          totalCustomers: parseInt(item.totalcustomer) || 0,
          directCustomers: parseInt(item.totaldirectcustomer) || 0,
          solutionPartners: parseInt(item.totalsolutionpartner) || 0
        })) || [];
        
        setMonthlyChartData(monthlyData);
      }
    } catch (error) {
      console.error('Error fetching monthly signup data:', error);
      message.error('Failed to load monthly signup data');
    }
  };

  const fetchCustomerData = async (page = 1, pageSize = 10) => {
    setTableLoading(true);
    try {
      const requestBody = {
        page: page,
        limit: pageSize
        // Show all customers for solution partner admin
      };

      const response = await axiosConfig.post('/company/getcompanydetails', requestBody);
      
      if (response.data?.status === true && response.data?.statuscode === 200) {
        const data = response.data.entity;
        
        // Transform data to include mock values for templates and campaigns
        const transformedData = (data.companies || []).map((company, _index) => ({
          ...company,
          totalContacts:0, // Mock data
          totalTemplates: 0, // Mock data
          totalCampaigns: 0, // Mock data
        }));

        setTableData(transformedData);
        setCompanyDetails(data);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: data.totalrecords || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      message.error('Failed to load customer data');
    } finally {
      setTableLoading(false);
    }
  };

  // Monthly customer chart data is now managed by state and API call

  // Handle table pagination change
  const handleTableChange = (pagination) => {
    fetchCustomerData(pagination.current, pagination.pageSize);
  };

  // Handle action buttons
  const handleViewCustomer = (record) => {
    message.info(`Viewing details for ${record.companyname}`);
  };

  const handleEditCustomer = (record) => {
    message.info(`Editing ${record.companyname}`);
  };

  const handleDeleteCustomer = (record) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this customer?',
      content: `This will permanently delete ${record.companyname} and all associated data.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        message.success(`${record.companyname} has been deleted`);
        fetchCustomerData(pagination.current, pagination.pageSize);
      },
    });
  };

  // Table columns configuration
  const tableColumns = [
    {
      title: 'Sr. No.',
      key: 'srno',
      width: 80,
      render: (_, record, index) => (
        <div style={{ textAlign: 'center', fontWeight: 500 }}>
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </div>
      ),
    },
    {
      title: 'Company Details',
      key: 'company',
      width: 250,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: 4 }}>
            {record.companyname || 'Unknown Company'}
          </div>
          <div style={{ fontSize: '12px', color: '#666', lineHeight: '18px' }}>
            <div><MailOutlined /> {record.emailid || 'N/A'}</div>
            <div><PhoneOutlined /> {record.contactno ? `${record.countrycode || ''}${record.contactno}` : 'N/A'}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tag color={record.status === 1 ? 'green' : 'red'}>
          {record.status === 1 ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Total Contacts',
      key: 'totalContacts',
      width: 120,
      render: (_, record) => (
        <div style={{ textAlign: 'center' }}>
          <Text strong style={{ color: '#1890ff' }}>
            {record.totalContacts?.toLocaleString() || '0'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Total Templates',
      key: 'totalTemplates',
      width: 130,
      render: (_, record) => (
        <div style={{ textAlign: 'center' }}>
          <Text strong style={{ color: '#52c41a' }}>
            {record.totalTemplates || '0'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Total Campaigns',
      key: 'totalCampaigns',
      width: 130,
      render: (_, record) => (
        <div style={{ textAlign: 'center' }}>
          <Text strong style={{ color: '#722ed1' }}>
            {record.totalCampaigns || '0'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Join Date',
      key: 'joinDate',
      width: 120,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          {moment(record.entrytime).format('MMM DD, YYYY')}
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button 
              type="text" 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => handleViewCustomer(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => handleEditCustomer(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button 
              type="text" 
              size="small" 
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDeleteCustomer(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Calculate growth percentage for current month
  const currentMonthGrowth = monthlyChartData.length > 1 
    ? ((monthlyChartData[monthlyChartData.length - 1]?.signups || 0) - 
       (monthlyChartData[monthlyChartData.length - 2]?.signups || 0)) /
      (monthlyChartData[monthlyChartData.length - 2]?.signups || 1) * 100
    : 0;

  // Calculate average monthly growth
  const averageMonthlyGrowth = monthlyChartData.length > 1 
    ? monthlyChartData.slice(1).reduce((sum, month, index) => {
        const prevMonth = monthlyChartData[index];
        const growth = prevMonth.signups > 0 
          ? ((month.signups - prevMonth.signups) / prevMonth.signups) * 100 
          : 0;
        return sum + growth;
      }, 0) / (monthlyChartData.length - 1)
    : 0;

  return (
    <div className="solution-partner-admin-dashboard">
      <Spin spinning={loading}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                Solution Partner Dashboard
              </Title>
              <Text type="secondary">
                Monitor your customer signups and manage your client base
              </Text>
            </Col>
            <Col>
              <Space>
                <Select
                  value={timeRange}
                  onChange={setTimeRange}
                  style={{ width: 140 }}
                  size="small"
                >
                  <Option value="4m">Last 4 months</Option>
                  <Option value="6m">Last 6 months</Option>
                  <Option value="1y">Last year</Option>
                </Select>
                <Button type="primary" size="small" icon={<ReloadOutlined />} onClick={() => {
                  fetchDashboardData();
                  fetchMonthlySignupData();
                }}>
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Signup Statistics Card */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={8}>
            <Card className="stats-card">
              <Statistic
                title="Total Customers"
                value={dashboardCounts.totalcustomer || 0}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card className="stats-card">
              <Statistic
                title="This Month Signups"
                value={monthlyChartData[monthlyChartData.length - 1]?.signups || 0}
                prefix={<UserOutlined />}
                suffix={
                  <span style={{ 
                    fontSize: '14px', 
                    color: currentMonthGrowth >= 0 ? '#52c41a' : '#ff4d4f',
                    marginLeft: '8px'
                  }}>
                    {currentMonthGrowth >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {Math.abs(currentMonthGrowth).toFixed(1)}%
                  </span>
                }
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card className="stats-card">
              <Statistic
                title="Average Monthly Growth"
                value={averageMonthlyGrowth}
                precision={1}
                suffix="%"
                prefix={<RiseOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Monthly Signups Chart */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <LineChartOutlined style={{ marginRight: '8px', color: '#722ed1' }} />
                    <span style={{ fontSize: '16px', fontWeight: 600 }}>Monthly Customer Signups</span>
                  </div>
                  <Badge 
                    count={`${monthlyChartData.length} months`} 
                    style={{ backgroundColor: '#722ed1' }}
                  />
                </div>
              }
              bordered={false}
              className="chart-card"
            >
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart 
                  data={monthlyChartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <defs>
                    <linearGradient id="signupGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#722ed1" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#722ed1" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="#f0f0f0"
                    vertical={false}
                  />
                  <XAxis 
                    dataKey="monthShort" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#8c8c8c', fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#8c8c8c', fontSize: 12 }}
                    dx={-10}
                  />
                  <RechartsTooltip 
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}
                    labelFormatter={(label, payload) => {
                      const data = payload?.[0]?.payload;
                      return data ? data.month : label;
                    }}
                    formatter={(value, _name) => [value, 'Signups']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="signups" 
                    stroke="#722ed1" 
                    strokeWidth={3}
                    fill="url(#signupGradient)"
                    dot={{ fill: '#722ed1', strokeWidth: 2, r: 6 }}
                    activeDot={{ r: 8, stroke: '#722ed1', strokeWidth: 2, fill: '#ffffff' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Customer Data Table */}
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TeamOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <span>Customer Management</span>
                  </div>
                  <Space>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Total: {companyDetails.totalrecords || 0} customers
                    </Text>
                    <Button size="small" icon={<FilterOutlined />}>
                      Filter
                    </Button>
                  </Space>
                </div>
              }
              bordered={false}
              className="table-card"
            >
              <Table
                dataSource={tableData}
                columns={tableColumns}
                loading={tableLoading}
                pagination={{
                  ...pagination,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} customers`,
                  pageSizeOptions: ['10', '20', '50', '100']
                }}
                onChange={handleTableChange}
                size="small"
                rowKey="companyid"
                scroll={{ x: 1200 }}
                className="customer-table"
              />
            </Card>
          </Col>
        </Row>

      </Spin>
    </div>
  );
};

export default SolutionPartnerAdminDashboard;
